import { lazy, Suspense, useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { LoginPage } from "@pages/LoginPage";
import { RegisterPage } from "@pages/RegisterPage";
import { DashboardPage } from "@pages/DashboardPage";
import { CallsPage } from "@pages/CallsPage";
import { HomePage } from "@pages/HomePage";
import { RouteFallback } from "@components/RouteFallback";
import { useAuth } from "@hooks/useAuth";
import { apiClient } from "@lib/apiClient";

// Code-split the heavy / rarely-used routes so the Twilio Video SDK
// (VideoPage / VideoJoinPage) and the admin/setup code don't ship in the
// initial bundle. Login / Register / Dashboard shell / Calls stay eager so the
// primary flows render instantly. Pages use named exports, so map to `default`.
const SetupPage = lazy(() => import("@pages/SetupPage").then((m) => ({ default: m.SetupPage })));
const PasswordResetPage = lazy(() =>
  import("@pages/PasswordResetPage").then((m) => ({ default: m.PasswordResetPage }))
);
const VideoJoinPage = lazy(() => import("@pages/VideoJoinPage").then((m) => ({ default: m.VideoJoinPage })));
const MessagesPage = lazy(() => import("@pages/MessagesPage").then((m) => ({ default: m.MessagesPage })));
const VideoPage = lazy(() => import("@pages/VideoPage").then((m) => ({ default: m.VideoPage })));
const ContactsPage = lazy(() => import("@pages/ContactsPage").then((m) => ({ default: m.ContactsPage })));
const NumbersPage = lazy(() => import("@pages/NumbersPage").then((m) => ({ default: m.NumbersPage })));
const SettingsPage = lazy(() => import("@pages/SettingsPage").then((m) => ({ default: m.SettingsPage })));
const InsightsPage = lazy(() => import("@pages/InsightsPage").then((m) => ({ default: m.InsightsPage })));
const AdminPage = lazy(() => import("@pages/AdminPage").then((m) => ({ default: m.AdminPage })));
const MorePage = lazy(() => import("@pages/MorePage").then((m) => ({ default: m.MorePage })));

export function App() {
  const { user, loading, login } = useAuth();
  const [configured, setConfigured] = useState<boolean | null>(null);

  useEffect(() => {
    apiClient
      .get<{ configured: boolean }>("/setup/status")
      .then((r) => setConfigured(r.configured))
      .catch(() => setConfigured(false));
  }, []);

  if (loading || configured === null) {
    return <RouteFallback />;
  }

  if (!configured) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="*" element={<SetupPage />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route
          path="/login"
          element={user ? <Navigate to="/dashboard" replace /> : <LoginPage onLogin={login} />}
        />
        <Route
          path="/register"
          element={user ? <Navigate to="/dashboard" replace /> : <RegisterPage onRegisterLogin={login} />}
        />
        <Route path="/password-reset" element={<PasswordResetPage />} />
        <Route path="/join" element={<VideoJoinPage />} />
        <Route
          path="/dashboard"
          element={user ? <DashboardPage /> : <Navigate to="/login" replace />}
        >
          <Route index element={<HomePage />} />
          <Route path="home" element={<HomePage />} />
          <Route path="calls" element={<CallsPage />} />
          <Route path="messages" element={<MessagesPage />} />
          <Route path="video" element={<VideoPage />} />
          <Route path="contacts" element={<ContactsPage />} />
          <Route path="numbers" element={<NumbersPage />} />
          <Route path="insights" element={<InsightsPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="more" element={<MorePage />} />
          <Route path="admin" element={user?.role === "SUPERADMIN" ? <AdminPage /> : <Navigate to="/dashboard" replace />} />
        </Route>
        <Route path="*" element={<Navigate to={user ? "/dashboard" : "/login"} replace />} />
      </Routes>
    </Suspense>
  );
}